/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    makeStyles,
    Grid,
    Divider,
} from '@material-ui/core';
const useStyles = makeStyles(theme => ({
    documentContainer: {
        marginBottom: theme.spacing(2),
        borderBottom: '1px solid #e0e0e0',
        paddingBottom: theme.spacing(1),
    },
    title: {
        fontWeight: 500,
    },
    linkButton: {
        color: '#fff',
        backgroundColor: '#B29A5E',
        marginRight: theme.spacing(1),
        '&:hover': {
            backgroundColor: '#B29A2E',
        },
    },
    gridContainer: {
        flexDirection: 'row',
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
        },
    },
    customCardContent: {
        padding: theme.spacing(2),
    },
    sectionTitle: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        fontWeight: 600,
        color: '#B29A5E',
    },
    expiryDate: {
        fontSize: '0.85rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    expired: {
        color: theme.palette.error.main,
        fontWeight: 500,
    },
}));

const DocumentLink = ({
    title,
    id,
    expiresAt
}: {
    title: string;
    id: any;
    expiresAt?: string | Date | null
}) => {
    const API_URL = process.env.REACT_APP_REST_API_URL;
    const viewLink = id ? `${API_URL}/files/${id}/view` : undefined;
    const downloadLink = id ? `${API_URL}/files/${id}/download` : undefined;

    const classes = useStyles();

    // Check if document is expired
    const expiryDate = expiresAt ? new Date(expiresAt) : null;
    const isExpired = expiryDate && expiryDate < new Date();
    const formattedDate = expiryDate
        ? expiryDate.toLocaleDateString('fr-FR')
        : null;

    return (
        <Grid item xs={12} sm={6}>
            <div className={classes.documentContainer}>
                <Typography
                    variant="body1"
                    gutterBottom
                    className={classes.title}
                >
                    {title}
                </Typography>
                {id ? (
                    <>
                        <Button
                            component="a"
                            href={viewLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            className={classes.linkButton}
                            disabled={!id}
                        >
                            Voir
                        </Button>

                        <Button
                            component="a"
                            href={downloadLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            className={classes.linkButton}
                            disabled={!id}
                        >
                            Télécharger
                        </Button>
                        {formattedDate && (
                            <Typography
                                variant="body2"
                                className={`${classes.expiryDate} ${
                                    isExpired ? classes.expired : ''
                                }`}
                            >
                                {isExpired ? '⚠️ Expiré le' : 'Expire le'}{' '}
                                {formattedDate}
                            </Typography>
                        )}
                    </>
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        (Aucune image n'est associée à ce document)
                    </Typography>
                )}
            </div>
        </Grid>
    );
};

export default function DocumentsTab(props: any) {
    const { record } = props;
    const classes = useStyles();

    const documents = record?.documents || {};
    const documentType = documents?.document_type || 'cni';

    return (
        <Card>
            <CardContent className={classes.customCardContent}>
                <Typography variant="h6" gutterBottom>
                    Documents du Chauffeur
                </Typography>

                {/* Identity Documents Section */}
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Documents d'identité ({documentType === 'passport' ? 'Passeport' : 'CNI'})
                </Typography>
                <Grid container spacing={2} className={classes.gridContainer}>
                    {documentType === 'passport' ? (
                        <DocumentLink
                            title="Passeport"
                            id={documents?.passport?.file}
                            expiresAt={documents?.passport?.expires_at}
                        />
                    ) : (
                        <>
                            <DocumentLink
                                title="CNI (Recto)"
                                id={documents?.cni?.recto_file}
                                expiresAt={documents?.cni?.expires_at}
                            />
                            <DocumentLink
                                title="CNI (Verso)"
                                id={documents?.cni?.verso_file}
                                expiresAt={documents?.cni?.expires_at}
                            />
                        </>
                    )}
                </Grid>

                <Divider style={{ margin: '16px 0' }} />

                {/* Banking Documents Section */}
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Documents bancaires
                </Typography>
                <Grid container spacing={2} className={classes.gridContainer}>
                    <DocumentLink
                        title="RIB (Relevé d'Identité Bancaire)"
                        id={documents?.rib?.file}
                    />
                </Grid>

                <Divider style={{ margin: '16px 0' }} />

                {/* Insurance Documents Section */}
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Assurances
                </Typography>
                <Grid container spacing={2} className={classes.gridContainer}>
                    <DocumentLink
                        title="Assurance Flotte / Véhicule"
                        id={documents?.assurance_flotte_vehicule?.file}
                        expiresAt={documents?.assurance_flotte_vehicule?.expires_at}
                    />
                    <DocumentLink
                        title="Attestation Assurance Responsabilité Civile"
                        id={documents?.attestation_assurance_resp_civile?.file}
                        expiresAt={documents?.attestation_assurance_resp_civile?.expires_at}
                    />
                    <DocumentLink
                        title="Attestation Assurance Responsabilité Contractuelle (Marchandise)"
                        id={documents?.attestation_assurance_resp_contractuelle?.file}
                        expiresAt={documents?.attestation_assurance_resp_contractuelle?.expires_at}
                    />
                </Grid>

                <Divider style={{ margin: '16px 0' }} />

                {/* Legal & Administrative Documents Section */}
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Documents légaux et administratifs
                </Typography>
                <Grid container spacing={2} className={classes.gridContainer}>
                    <DocumentLink
                        title="KBIS / RCS"
                        id={documents?.kbis?.file}
                        expiresAt={documents?.kbis?.expires_at}
                    />
                    <DocumentLink
                        title="Attestation de Vigilance URSSAF"
                        id={documents?.attestation_vigilance_urssaf?.file}
                        expiresAt={documents?.attestation_vigilance_urssaf?.expires_at}
                    />
                    <DocumentLink
                        title="Attestation Travailleurs Étrangers"
                        id={documents?.attestation_travailleurs_etrangers?.file}
                        expiresAt={documents?.attestation_travailleurs_etrangers?.expires_at}
                    />
                    <DocumentLink
                        title="Licence Intracommunautaire"
                        id={documents?.licence_intracommunautaire?.file}
                        expiresAt={documents?.licence_intracommunautaire?.expires_at}
                    />
                </Grid>
            </CardContent>
        </Card>
    );
}
